import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Star, ShoppingCart, Check } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useCart } from '../context/CartContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

const BACKEND_URL = "https://seedoza-backend1.onrender.com";
const API = `${BACKEND_URL}/api`;

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [product, setProduct] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [reviews, setReviews] = useState([]);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewForm, setReviewForm] = useState({
    customer_name: '',
    rating: 5,
    comment: ''
  });

  useEffect(() => {
    fetchProduct();
    fetchReviews();
  }, [id]);

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API}/products/${id}`);
      setProduct(response.data);
      setSelectedVariant(response.data.variants[0]);
    } catch (error) {
      console.error('Error fetching product:', error);
      toast.error('Product not found');
      navigate('/products');
    }
  };

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/reviews/${id}`);
      setReviews(response.data);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  };

  const handleAddToCart = () => {
    if (!selectedVariant) {
      toast.error('Please select a variant');
      return;
    }

    const cartItem = {
      product_id: product.id,
      product_name: product.name,
      variant_size: selectedVariant.size,
      price: selectedVariant.price,
      quantity: quantity,
      image: product.images[0]
    };

    addToCart(cartItem);
    toast.success('Added to cart!');
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/reviews`, {
        product_id: id,
        ...reviewForm
      });
      toast.success('Review submitted!');
      setShowReviewForm(false);
      setReviewForm({ customer_name: '', rating: 5, comment: '' });
      fetchReviews();
    } catch (error) {
      console.error('Error submitting review:', error);
      toast.error('Failed to submit review');
    }
  };

  if (!product) {
    return (
      <div className="section-spacing">
        <div className="container-custom text-center">
          <p className="text-lg">Loading...</p>
        </div>
      </div>
    );
  }

  const averageRating = reviews.length > 0
    ? (reviews.reduce((acc, r) => acc + r.rating, 0) / reviews.length).toFixed(1)
    : 0;

  return (
    <div className="section-spacing" data-testid="product-detail-page">
      <div className="container-custom">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-12 mb-12 md:mb-16">
          {/* Product Images */}
          <div className="space-y-4">
            <div className="aspect-square rounded-2xl md:rounded-3xl overflow-hidden bg-white">
              <img
                src={product.images[0]}
                alt={product.name}
                className="w-full h-full object-cover"
                data-testid="product-image"
              />
            </div>
          </div>

          {/* Product Info */}
          <div className="space-y-4 md:space-y-6">
            <div>
              <h1 className="text-2xl md:text-4xl font-heading font-bold mb-3 md:mb-4" data-testid="product-name">{product.name}</h1>
              <div className="flex items-center space-x-4 mb-3 md:mb-4">
                <div className="flex items-center space-x-1">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className={`w-4 h-4 md:w-5 md:h-5 ${i < averageRating ? 'fill-accent text-accent' : 'text-gray-300'}`}
                    />
                  ))}
                </div>
                <span className="text-sm md:text-base text-muted-foreground">({reviews.length} reviews)</span>
              </div>
              <p className="text-sm md:text-lg text-muted-foreground" data-testid="product-description">{product.description}</p>
            </div>

            {/* Variants */}
            <div>
              <label className="block text-sm font-medium mb-2 md:mb-3">Select Size:</label>
              <div className="grid grid-cols-3 gap-2 md:gap-3">
                {product.variants.map((variant, idx) => (
                  <button
                    key={idx}
                    data-testid={`variant-${variant.size}`}
                    onClick={() => setSelectedVariant(variant)}
                    className={`p-3 md:p-4 rounded-lg border-2 transition-all ${
                      selectedVariant?.size === variant.size
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-primary/50'
                    }`}
                  >
                    <div className="font-semibold text-sm md:text-base">{variant.size}</div>
                    <div className="text-xs md:text-sm text-muted-foreground">₹{variant.price}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Price */}
            <div className="flex items-baseline space-x-2">
              <span className="text-3xl md:text-4xl font-bold text-primary" data-testid="product-price">
                ₹{selectedVariant?.price}
              </span>
            </div>

            {/* Quantity */}
            <div>
              <label className="block text-sm font-medium mb-2 md:mb-3">Quantity:</label>
              <div className="flex items-center space-x-4">
                <button
                  data-testid="decrease-quantity"
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="w-11 h-11 md:w-10 md:h-10 rounded-full border-2 border-border hover:border-primary transition-colors text-lg font-semibold"
                >
                  -
                </button>
                <span className="text-xl font-semibold w-12 text-center" data-testid="quantity-display">{quantity}</span>
                <button
                  data-testid="increase-quantity"
                  onClick={() => setQuantity(quantity + 1)}
                  className="w-11 h-11 md:w-10 md:h-10 rounded-full border-2 border-border hover:border-primary transition-colors text-lg font-semibold"
                >
                  +
                </button>
              </div>
            </div>

            {/* Add to Cart */}
            <button
              data-testid="add-to-cart-button"
              onClick={handleAddToCart}
              className="btn-accent w-full flex items-center justify-center space-x-2 py-4"
            >
              <ShoppingCart className="w-5 h-5" />
              <span>Add to Cart</span>
            </button>

            {/* Benefits */}
            <div className="border-t border-border pt-4 md:pt-6">
              <h3 className="text-lg md:text-xl font-heading font-semibold mb-3 md:mb-4">Benefits:</h3>
              <ul className="space-y-2">
                {product.benefits.map((benefit, idx) => (
                  <li key={idx} className="flex items-start space-x-2 text-sm md:text-base">
                    <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Ingredients */}
            <div className="border-t border-border pt-4 md:pt-6">
              <h3 className="text-lg md:text-xl font-heading font-semibold mb-3 md:mb-4">Ingredients:</h3>
              <div className="flex flex-wrap gap-2">
                {product.ingredients.map((ingredient, idx) => (
                  <span key={idx} className="bg-muted px-3 md:px-4 py-1.5 md:py-2 rounded-full text-xs md:text-sm">
                    {ingredient}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* The Power of Super Seeds Section */}
        <div className="border-t border-border pt-10 md:pt-16">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-2xl md:text-4xl font-heading font-bold mb-3 md:mb-4">The Power of Super Seeds</h2>
            <p className="text-sm md:text-lg text-muted-foreground max-w-2xl mx-auto">
              Every ingredient is carefully selected for maximum nutrition
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6 mb-10 md:mb-16">
            <div className="feature-card">
              <h3 className="text-base md:text-2xl font-heading font-semibold mb-1 md:mb-2">Pumpkin Seeds</h3>
              <p className="text-xs md:text-base text-primary font-medium">High in protein & zinc</p>
            </div>

            <div className="feature-card">
              <h3 className="text-base md:text-2xl font-heading font-semibold mb-1 md:mb-2">Sunflower Seeds</h3>
              <p className="text-xs md:text-base text-primary font-medium">Rich in vitamin E</p>
            </div>

            <div className="feature-card">
              <h3 className="text-base md:text-2xl font-heading font-semibold mb-1 md:mb-2">Flax Seeds</h3>
              <p className="text-xs md:text-base text-primary font-medium">Loaded with omega-3</p>
            </div>

            <div className="feature-card">
              <h3 className="text-base md:text-2xl font-heading font-semibold mb-1 md:mb-2">Sesame Seeds</h3>
              <p className="text-xs md:text-base text-primary font-medium">Full of minerals</p>
            </div>

            <div className="feature-card">
              <h3 className="text-base md:text-2xl font-heading font-semibold mb-1 md:mb-2">Dates</h3>
              <p className="text-xs md:text-base text-primary font-medium">Natural sweetness</p>
            </div>

            <div className="feature-card">
              <h3 className="text-base md:text-2xl font-heading font-semibold mb-1 md:mb-2">Dark Chocolate</h3>
              <p className="text-xs md:text-base text-primary font-medium">Antioxidants</p>
            </div>
          </div>
        </div>

        {/* Reviews Section */}
        <div className="border-t border-border pt-10 md:pt-16">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 md:mb-8">
            <h2 className="text-xl md:text-3xl font-heading font-bold">Customer Reviews</h2>
            <Button
              data-testid="write-review-button"
              onClick={() => setShowReviewForm(!showReviewForm)}
              className="btn-outline w-full sm:w-auto"
            >
              Write a Review
            </Button>
          </div>

          {/* Review Form */}
          {showReviewForm && (
            <form onSubmit={handleSubmitReview} className="bg-muted/30 p-6 rounded-2xl mb-8" data-testid="review-form">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Your Name</label>
                  <Input
                    data-testid="review-name-input"
                    value={reviewForm.customer_name}
                    onChange={(e) => setReviewForm({ ...reviewForm, customer_name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Rating</label>
                  <div className="flex space-x-2">
                    {[1, 2, 3, 4, 5].map((rating) => (
                      <button
                        key={rating}
                        type="button"
                        data-testid={`rating-${rating}`}
                        onClick={() => setReviewForm({ ...reviewForm, rating })}
                        className="focus:outline-none"
                      >
                        <Star
                          className={`w-8 h-8 ${rating <= reviewForm.rating ? 'fill-accent text-accent' : 'text-gray-300'}`}
                        />
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Your Review</label>
                  <Textarea
                    data-testid="review-comment-input"
                    value={reviewForm.comment}
                    onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })}
                    rows={4}
                    required
                  />
                </div>
                <div className="flex space-x-4">
                  <Button type="submit" className="btn-primary" data-testid="submit-review-button">
                    Submit Review
                  </Button>
                  <Button
                    type="button"
                    onClick={() => setShowReviewForm(false)}
                    className="btn-outline"
                    data-testid="cancel-review-button"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </form>
          )}

          {/* Reviews List */}
          <div className="space-y-6">
            {reviews.length > 0 ? (
              reviews.map((review) => (
                <div key={review.id} className="feature-card" data-testid={`review-${review.id}`}>
                  <div className="flex items-center space-x-4 mb-3">
                    <div className="flex items-center space-x-1">
                      {[...Array(review.rating)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 fill-accent text-accent" />
                      ))}
                    </div>
                    <span className="font-semibold">{review.customer_name}</span>
                  </div>
                  <p className="text-muted-foreground">{review.comment}</p>
                </div>
              ))
            ) : (
              <p className="text-center text-muted-foreground py-8">No reviews yet. Be the first to review!</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
